import React, { useRef } from 'react';
import { Button, Box } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PropTypes from 'prop-types';

/**
 * FileUpload - MUI-based file upload component
 * Originally from ReactMdCompat layer, now standalone MUI component
 */
/* eslint-disable no-unused-vars */
export const FileUpload = React.forwardRef(({
  id,
  name,
  label,
  className,
  style,
  sx,
  onChange,
  onLoad,
  onLoadStart,
  onLoadEnd,
  onProgress,
  onAbort,
  onError,
  disabled,
  accept,
  multiple,
  primary,
  secondary,
  flat,
  raised,
  icon,
  iconBefore = true,
  iconChildren,
  iconClassName,
  maxSize,
  allowDuplicates,
  readAs,
  ...props
}, ref) => {
  /* eslint-enable no-unused-vars */
  const inputRef = useRef(null);

  const handleClick = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleChange = (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    // Call onChange with files
    if (onChange) {
      onChange(files, event);
    }

    // If readAs is specified, read the files
    if (onLoad) {
      Array.from(files).forEach((file) => {
        const reader = new FileReader();
        
        reader.onloadstart = () => {
          if (onLoadStart) onLoadStart(file);
        };
        
        reader.onprogress = (e) => {
          if (onProgress) onProgress(file, e);
        };
        
        reader.onload = (e) => {
          onLoad(file, e.target.result);
        };
        
        reader.onerror = (e) => {
          if (onError) onError(file, e);
        };
        
        reader.onabort = (e) => {
          if (onAbort) onAbort(file, e);
        };
        
        reader.onloadend = () => {
          if (onLoadEnd) onLoadEnd(file);
        };

        switch (readAs) {
          case 'DataURL':
          case 'dataURL':
            reader.readAsDataURL(file);
            break;
          case 'ArrayBuffer':
          case 'arrayBuffer':
            reader.readAsArrayBuffer(file);
            break;
          case 'Text':
          case 'text':
            reader.readAsText(file);
            break;
          case 'BinaryString':
          case 'binaryString':
            reader.readAsBinaryString(file);
            break;
          default:
            reader.readAsDataURL(file);
        }
      });
    }

    // Reset input to allow selecting same file again
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const buttonIcon = icon !== false ? (iconChildren || <CloudUploadIcon />) : null;

  return (
    <Box className={className} style={style} sx={sx}>
      <input
        ref={inputRef}
        type="file"
        id={id}
        name={name}
        accept={accept}
        multiple={multiple}
        onChange={handleChange}
        disabled={disabled}
        style={{ display: 'none' }}
        {...props}
      />
      <Button
        onClick={handleClick}
        disabled={disabled}
        variant={raised ? 'contained' : 'text'}
        color={primary ? 'primary' : secondary ? 'secondary' : 'inherit'}
        startIcon={iconBefore ? buttonIcon : undefined}
        endIcon={!iconBefore ? buttonIcon : undefined}
      >
        {label || 'Upload File'}
      </Button>
    </Box>
  );
});

FileUpload.displayName = 'FileUpload';

FileUpload.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.node,
  className: PropTypes.string,
  style: PropTypes.object,
  sx: PropTypes.object,
  onChange: PropTypes.func,
  onLoad: PropTypes.func,
  onLoadStart: PropTypes.func,
  onLoadEnd: PropTypes.func,
  onProgress: PropTypes.func,
  onAbort: PropTypes.func,
  onError: PropTypes.func,
  disabled: PropTypes.bool,
  accept: PropTypes.string,
  multiple: PropTypes.bool,
  primary: PropTypes.bool,
  secondary: PropTypes.bool,
  flat: PropTypes.bool,
  raised: PropTypes.bool,
  icon: PropTypes.bool,
  iconBefore: PropTypes.bool,
  iconChildren: PropTypes.node,
  iconClassName: PropTypes.string,
  maxSize: PropTypes.number,
  allowDuplicates: PropTypes.bool,
  readAs: PropTypes.oneOf(['DataURL', 'dataURL', 'ArrayBuffer', 'arrayBuffer', 'Text', 'text', 'BinaryString', 'binaryString']),
};

export default FileUpload;
